import { styled } from "@material-ui/core";
import PropTypes from "prop-types";
import React, { forwardRef } from "react";
import { colors } from "../../styles/colors";

const defaultTypography = {
    h1: { fontSize: "3rem", lineHeight: "3.75rem" },
    h2: { fontSize: "2.25rem", lineHeight: "2.875rem" },
    h3: { fontSize: "1.75rem", lineHeight: "2.5rem" },
    h4: { fontSize: "1.5rem", lineHeight: "2.125rem" },
    h5: { fontSize: "1.375rem", lineHeight: "2rem" },
    h6: { fontSize: "1.25rem", lineHeight: "1.875rem" },
    h7: { fontSize: "1.125rem", lineHeight: "1.75rem" }, // Note: <h7> is not a standard HTML tag, but styled('h7') will render it as a custom tag.

    b1: { fontSize: "1rem", lineHeight: "1.5rem" },
    b2: { fontSize: "0.875rem", lineHeight: "1.375rem" },
    b3: { fontSize: "0.75rem", lineHeight: "1.25rem" },
    b4: { fontSize: "0.625rem", lineHeight: "1rem" },
    b5: { fontSize: "0.5rem", lineHeight: "0.875rem" },
}

const CustomH1 = styled('h1')(({ variant, color }) => {
    return {
        ...defaultTypography.h1,
        fontWeight: variant === 'h1B' ? 700 : variant === 'h1SB' ? 600 : variant === 'h1M' ? 500 : 400,
        color: color
    }
});

const CustomH2 = styled('h2')(({ variant, color }) => {
    return {
        ...defaultTypography.h2,
        fontWeight: variant === 'h2B' ? 700 : variant === 'h2SB' ? 600 : variant === 'h2M' ? 500 : 400,
        color: color
    }
});

const CustomH3 = styled('h3')(({ variant, color }) => {
    return {
        ...defaultTypography.h3,
        fontWeight: variant === 'h3B' ? 700 : variant === 'h3SB' ? 600 : variant === 'h3M' ? 500 : 400,
        color: color
    }
});

const CustomH4 = styled('h4')(({ variant, color }) => {
    return {
        ...defaultTypography.h4,
        fontWeight: variant === 'h4B' ? 700 : variant === 'h4SB' ? 600 : variant === 'h4M' ? 500 : 400,
        color: color
    }
});

const CustomH5 = styled('h5')(({ variant, color }) => {
    return {
        ...defaultTypography.h5,
        fontWeight: variant === 'h5B' ? 700 : variant === 'h5SB' ? 600 : variant === 'h5M' ? 500 : 400,
        color: color
    }
});

const CustomH6 = styled('h6')(({ variant, color }) => {
    return {
        ...defaultTypography.h6,
        fontWeight: variant === 'h6B' ? 700 : variant === 'h6SB' ? 600 : variant === 'h6M' ? 500 : 400,
        color: color
    }
});

const CustomH7 = styled('h7')(({ variant, color }) => {
    return {
        ...defaultTypography.h7,
        fontWeight: variant === 'h7B' ? 700 : variant === 'h7SB' ? 600 : variant === 'h7M' ? 500 : 400,
        color: color
    }
});

const CustomP = styled('p')(({ variant = 'b1R', color }) => {
    const weight = variant.slice(2);

    return {
        ...defaultTypography[variant?.slice(0, 2)],
        fontWeight: weight === 'B' ? 700 : weight === 'SB' ? 600 : weight === 'M' ? 500 : 400,
        color: color
    }
});

// 1. Wrap the component in forwardRef
const Typography = forwardRef(({ children, variant, color = colors['grey900'], style, ...props }, ref) => {
    // 2. Pass 'ref' and spread '...props' to every styled component
    switch (variant?.slice(0, 2)) {
        case 'h1':
            return (
                <CustomH1 ref={ref} variant={variant} color={color} style={style} {...props}>
                    {children}
                </CustomH1>
            );
        case 'h2':
            return (
                <CustomH2 ref={ref} variant={variant} color={color} style={style} {...props}>
                    {children}
                </CustomH2>
            );
        case 'h3':
            return (
                <CustomH3 ref={ref} variant={variant} color={color} style={style} {...props}>
                    {children}
                </CustomH3>
            );
        case 'h4':
            return (
                <CustomH4 ref={ref} variant={variant} color={color} style={style} {...props}>
                    {children}
                </CustomH4>
            );
        case 'h5':
            return (
                <CustomH5 ref={ref} variant={variant} color={color} style={style} {...props}>
                    {children}
                </CustomH5>
            );
        case 'h6':
            return (
                <CustomH6 ref={ref} variant={variant} color={color} style={style} {...props}>
                    {children}
                </CustomH6>
            );
        case 'h7':
            return (
                <CustomH7 ref={ref} variant={variant} color={color} style={style} {...props}>
                    {children}
                </CustomH7>
            );
        default:
            return (
                <CustomP ref={ref} variant={variant} color={color} style={style} {...props}>
                    {children}
                </CustomP>
            );
    }
});

// Set the display name for better debugging in React DevTools
Typography.displayName = 'Typography';

export default Typography;

Typography.propTypes = {
    color: PropTypes.string,
    style: PropTypes.object,
    children: PropTypes.node,
    variant: PropTypes.oneOf([
        'h1R', 'h1M', 'h1SB', 'h1B',
        'h2R', 'h2M', 'h2SB', 'h2B',
        'h3R', 'h3M', 'h3SB', 'h3B',
        'h4R', 'h4M', 'h4SB', 'h4B',
        'h5R', 'h5M', 'h5SB', 'h5B',
        'h6R', 'h6M', 'h6SB', 'h6B',
        'h7R', 'h7M', 'h7SB', 'h7B',
        'b1R', 'b1M', 'b1SB', 'b1B',
        'b2R', 'b2M', 'b2SB', 'b2B',
        'b3R', 'b3M', 'b3SB', 'b3B',
        'b4R', 'b4M', 'b4SB', 'b4B',
        'b5R', 'b5M', 'b5SB', 'b5B',
    ]),
};